import { useCallback, useEffect, useMemo } from "react"
import type { UseControllerProps, Validate, ValidationRule, ValidationValueMessage } from "react-hook-form"
import { useController, useFormContext } from "react-hook-form"
import { defineMessages, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { Tooltip } from "../../tooltip/Tooltip"
import { InputNumber } from "../inputNumber"
import type { InputNumberValue } from "../inputNumber"
import type { InputProps, InputValue } from "./Input"
import { Input } from "./Input"

const messages = defineMessages({
	emailNotValid: {
		defaultMessage: "Not a valid E-Mail address",
		id: "form.error.email",
	},
	patternNotValid: {
		defaultMessage: "Invalid input",
		id: "form.error.pattern",
	},
	urlNotValid: {
		defaultMessage: "Not a valid URL",
		id: "form.error.url",
	},
})

type PatternType = ValidationRule<RegExp> | ValidationValueMessage<RegExp>

export interface InputFieldProps extends Omit<InputProps, "name" | "value" | "onChange" | "type" | "pattern"> {
	disabledHint?: string
	max?: number
	min?: number
	name: string
	onChange?: (value: InputValue | InputNumberValue) => void
	pattern?: PatternType
	shouldSuppressErrors?: boolean
	type: "text" | "password" | "search" | "number" | "currency" | "email" | "url"
	validate?: Validate<any, any>
}

const emailRegExp = /^[\w-.]+@([\w-]+\.)+[\w-]+$/
const urlPattern = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/

export const InputField = (props: InputFieldProps) => {
	const {
		disabled,
		disabledHint,
		max,
		maxLength,
		min,
		minLength,
		name,
		onChange,
		pattern,
		required,
		shouldSuppressErrors,
		type,
		validate,
		...rest
	} = props

	const { formatMessage } = useIntl()
	const { control, trigger } = useFormContext()

	const patternType: PatternType | undefined = useMemo(() => {
		if (pattern) {
			return "message" in pattern
				? pattern
				: {
						message: formatMessage(messages.patternNotValid),
						value: pattern,
					}
		}

		if (type === "email") {
			return {
				message: formatMessage(messages.emailNotValid),
				value: emailRegExp,
			}
		}

		if (type === "url") {
			return {
				message: formatMessage(messages.urlNotValid),
				value: urlPattern,
			}
		}

		return undefined
	}, [pattern, type, formatMessage])

	const rules: UseControllerProps["rules"] = useMemo(() => {
		return {
			max: max
				? {
						message: formatMessage(formMessages.max, { max }),
						value: max,
					}
				: undefined,
			maxLength:
				maxLength !== undefined
					? {
							message: formatMessage(formMessages.maxLength, { value: maxLength }),
							value: maxLength,
						}
					: undefined,
			min: min
				? {
						message: formatMessage(formMessages.min, { min }),
						value: min,
					}
				: undefined,
			minLength:
				minLength !== undefined
					? {
							message: formatMessage(formMessages.minLength, { value: minLength }),
							value: minLength,
						}
					: undefined,
			pattern: patternType,
			required: {
				message: formatMessage(formMessages.required),
				value: !!required,
			},
			validate,
		}
	}, [max, min, required, formatMessage, validate, patternType, minLength, maxLength])

	const {
		field: { onChange: fieldOnChange, ref, value },
		fieldState: { error, isDirty },
		formState: { isSubmitted },
	} = useController({
		control,
		name,
		rules,
		shouldUnregister: true,
	})

	// Fields need to retrigger validation in order to apply changed rules, since the rules are cached inside react-hook-form.
	// See https://github.com/react-hook-form/react-hook-form/issues/1749 and https://github.com/react-hook-form/react-hook-form/issues/8310
	// An error of the server is kept until the user changes the field, validating again would remove it.
	const hasServerError = error?.type === "server"
	useEffect(() => {
		if (!shouldSuppressErrors && isSubmitted && !hasServerError) {
			trigger(name)
		}
	}, [shouldSuppressErrors, rules, trigger, name, isDirty, isSubmitted, hasServerError])

	const handleChange = useCallback(
		(newValue: InputValue | InputNumberValue) => {
			fieldOnChange(newValue ?? null)
			onChange?.(newValue)
		},
		[fieldOnChange, onChange],
	)

	const inputProps = {
		"data-testid": `input-field_${name}`,
		...rest,
		disabled: !!disabledHint || disabled,
		errorMessage: error?.message,
		maxLength,
		minLength,
		name,
		onChange: handleChange,
		ref,
		required: !!required,
		value,
	}

	const input =
		type === "currency" || type === "number" ? (
			<InputNumber {...inputProps} type={type} />
		) : (
			<Input {...inputProps} type={type} />
		)

	return <Tooltip content={disabledHint}>{input}</Tooltip>
}
